import React, { useState, useRef } from 'react';
import { Upload, Image, AlertCircle } from 'lucide-react';
import { analyzeWireframe } from '../services/LLMCalls';

const GetDescription = ({ onDescriptionGenerated, initialValue = '', disabled = false, projectId }) => {
  const [description, setDescription] = useState(initialValue);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // File validation
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  const validateFile = (file) => {
    if (!file) return null;
    
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return `File type not supported. Please upload ${ALLOWED_FILE_TYPES.map(type => type.split('/')[1].toUpperCase()).join(', ')}`;
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return `File is too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`;
    }
    
    return null;
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

const handleFileUpload = async (file) => {
  const validationError = validateFile(file);
  if (validationError) {
    setError(validationError);
    setImagePreview(null); // <-- CRITICAL FIX: Clear stale image preview
    return;
  }

  try {
    setIsProcessing(true);
    setError(null);
    
    // Create image preview
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);

    // Convert file to base64
    const base64Image = await convertToBase64(file);
    
    // Send to backend for LLM processing
    const response = await analyzeWireframe(base64Image, projectId);

    // Update description state and callback
    const generatedDescription = response.description;
    setDescription(generatedDescription);
    onDescriptionGenerated(generatedDescription);
    
  } catch (error) {
    console.error('Error processing wireframe:', error);
    setError(error.response?.data?.message || error.message || 'Failed to process wireframe. Please try again.');
  } finally {
    setIsProcessing(false);
  }
};

  const handleChange = (e) => {
    const newValue = e.target.value;
    setDescription(newValue);
    onDescriptionGenerated(newValue);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

const handleRetry = () => {
  setError(null);
  setImagePreview(null);
  setDescription(initialValue); // <-- CRITICAL FIX: Reset description to initial state
  if (fileInputRef.current) {
    fileInputRef.current.value = '';
  }
};

  // If we have a description already, show the text area for editing
  if (description) {
    return (
      <div>
        <textarea
          value={description}
          onChange={handleChange}
          rows={10}
          placeholder="UI description generated from your wireframe..."
          className="w-full p-3 border rounded-md"
          disabled={disabled}
        />
      </div>
    );
  }

  // Show loading state when processing
  if (isProcessing) {
    return (
      <div className="w-full p-8 border-2 border-dashed rounded-md text-center bg-gray-50">
        <div className="mb-4 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black"></div>
        </div>
        <p className="text-gray-600">Processing your wireframe...</p>
        {imagePreview && (
          <div className="mt-4">
            <img 
              src={imagePreview} 
              alt="Wireframe preview" 
              className="max-w-xs mx-auto max-h-48 object-contain" 
            />
          </div>
        )}
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-full p-8 border-2 border-dashed border-red-300 rounded-md text-center bg-red-50">
        <div className="mb-4 flex justify-center text-red-500">
          <AlertCircle size={32} />
        </div>
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={handleRetry}
          className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
        >
          Try Again
        </button>
        {imagePreview && (
          <div className="mt-4">
            <img 
              src={imagePreview} 
              alt="Wireframe preview" 
              className="max-w-xs mx-auto max-h-48 object-contain opacity-50" 
            />
          </div>
        )}
      </div>
    );
  }

  // Default upload state
  return (
    <div
      className={`w-full p-8 border-2 border-dashed rounded-md text-center transition-colors ${
        isDragging ? 'border-black bg-gray-100' : 'border-gray-300 bg-gray-50'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept={ALLOWED_FILE_TYPES.join(',')}
        className="hidden"
        disabled={disabled}
      />
      
      <div className="mb-4 flex justify-center">
        <Image size={32} className="text-gray-400" />
      </div>
      
      <p className="mb-2 text-gray-600">
        Drag and drop your wireframe image here, or
      </p>
      
      <button
        type="button"
        onClick={triggerFileInput}
        disabled={disabled}
        className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        <Upload className="w-4 h-4 inline mr-2" />
        Browse Files
      </button>
      
      <p className="mt-2 text-xs text-gray-500">
        Upload a wireframe, mockup, or screenshot (JPEG, PNG, GIF, WEBP - max 5MB)
      </p>
    </div>
  );
};

export default GetDescription;